import { Database } from 'firebase-firestore-lite'
import { Controller } from '@hotwired/stimulus'
import FlowController from './flow_controller'
import {
  getSetUserDataFn,
  getPageNavHandlers,
  getOpenInfoBoxFn,
  trackCustomEvent,
} from '../helpers/controllerHelpers/utils'
import {
  generateId,
  hasEnvProps,
  hasUrlParam,
  isBuilderEditingMode,
  initCommandBar,
  addDomElementToHead,
  initAndWaitForSupabaseClient,
  createAndInsertCustomFontTag,
} from '../helpers/utils'
import Auth from '../helpers/auth'
import { handleRequest } from '../api/handleRequest'
import setUpCommandBar from '../helpers/command_bar'

import type {
  UserData,
  Flow,
  Version,
  Status,
  EventLog,
  Context,
  ResetUserDataOptions,
  Tag,
  TagFontMetadata,
} from '../api/models/types'

// type FlowController = {
//   setUserData: (key, value) => void
// }

interface idOrKeyOrIndex {
  id?: string
  index?: number
  key?: string
}

// const supabaseClient = await initAndWaitForSupabaseClient()

export default class GlobalController extends Controller {
  declare selectedFlowIdValue: string | null
  declare selectedFlow: string | null
  declare selectedFlowJson: Flow | null
  declare resetWindowFunctionsValue: boolean

  static values = {
    selectedFlowId: String,
    resetWindowFunctions: Boolean,
  }

  async connect() {
    this.setupWindowFunctions()

    //* If no flow is selected, I select the first flow on the page
    this.trySyncSelectedFlowFromSavvyHost()

    //* I append the font use in the builder
    addDomElementToHead('link', {
      href: 'https://fonts.googleapis.com/css2?family=Work+Sans:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900&display=swap',
      rel: 'stylesheet',
    })

    //* I append CommandBar if wanted
    if (
      hasUrlParam('builder_lite', 'true') ||
      hasUrlParam('builder-lite', 'true') ||
      (window.location.hostname.startsWith('preview') &&
        window.location.hostname.split('.').length > 2) ||
      window.location.hostname.startsWith('localhost') ||
      isBuilderEditingMode()
    ) {
      initCommandBar()
      setUpCommandBar(this.selectedFlowIdValue || '')
    }

    if (hasUrlParam('savvy_auth', 'true')) new Auth()
  }

  /**
   * We're just moving the definitions of the window functions to a separate method
   * so that we can call it again if we need to re-initialize the window functions
   */
  setupWindowFunctions(forced?: boolean) {
    if (!window.Savvy || forced) window.Savvy = {}
    if (!window.Embeddables || forced) window.Embeddables = {}

    window.Savvy.getGroupId = () => window.Savvy.group_id || null

    window.Savvy.getAllFlowsOnPage = () => {
      const flows: { flowId: string; flow: Flow }[] = []
      document.querySelectorAll('savvy').forEach((el) => {
        const flowId = this.resolveFlowIdFromSavvyElement(el)
        const flow = flowId && this.getFlowController(flowId)?.getFlow(true)
        if (flowId && flow && !flows.some((f) => f.flowId === flowId)) flows.push({ flowId, flow })
      })
      return flows
    }

    // Makes sure this is overridden by the global-edit controller's version if it exists
    if (!window.Savvy.getFlowJson)
      window.Savvy.getFlowJson = window.Embeddables.getEmbeddableJson = (flowIdOrElement) =>
        this.getFlowController(flowIdOrElement)?.getFlow(true)

    // Makes sure this is overridden by the global-edit controller's version if it exists
    if (!window.Savvy.setFlowJson)
      window.Savvy.setFlowJson = window.Embeddables.setEmbeddableJson = (flowIdOrElement, json) =>
        this.getFlowController(flowIdOrElement)?.setFlow(json)

    window.Savvy.getFlowSelection = () => ({
      flowId: this.selectedFlowIdValue,
      flow_id: this.selectedFlowIdValue,
      flow: this.selectedFlowJson,
    })

    window.Savvy.getUserData = window.Embeddables.getUserData = (flowIdOrElement) =>
      this.getFlowController(flowIdOrElement || this.selectedFlow)?.userDataValue as UserData

    window.Savvy.setUserDataProperty = window.Embeddables.setUserDataProperty = (
      flowIdOrElement,
      key,
      value,
      immediate
    ) =>
      getSetUserDataFn(this.getFlowController(flowIdOrElement || this.selectedFlow))(
        key,
        value,
        immediate
      )

    window.Savvy.setUserData = window.Embeddables.setUserData = (
      flowIdOrElement,
      data,
      replaceAll?,
      immediate?
    ) =>
      this.getFlowController(flowIdOrElement || this.selectedFlow)?.batchUpdate({
        detail: {
          updates: data,
          replace_user_data: replaceAll,
          immediate,
        },
      })

    window.Savvy.resetUserData = window.Embeddables.resetUserData = (
      flowIdOrElement,
      options?: ResetUserDataOptions
    ) => {
      this.getFlowController(flowIdOrElement || this.selectedFlow)?.reset(options)
    }

    window.Savvy.openInfoBox = window.Embeddables.openInfoBox = async (
      flowIdOrElement,
      infoBoxKey
    ) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const openInfoBox = await getOpenInfoBoxFn(flowController)
      return openInfoBox({ detail: { value: infoBoxKey } })
    }

    window.Savvy.getEventLogs = (flowId) =>
      (window.Savvy.event_logs || []).filter((log) => log.flow_id === (flowId || this.selectedFlow))
    window.Savvy.addEventLogs = (logs) => {
      if (hasEnvProps(['debug', 'editing', 'test'])) {
        if (!window.Savvy) window.Savvy = {}
        window.Savvy.event_logs = window.Savvy.event_logs || []
        const withLogId = logs.map((log) => ({ ...log, log_id: generateId('log') })) as EventLog[]
        window.Savvy.event_logs.push(...withLogId)
        // Send a window event with name 'savvy:event_log_updated'
        const event = new CustomEvent('savvy:event_logs_updated', {
          detail: { logs: withLogId },
        })
        window.dispatchEvent(event)
      }
    }

    window.Savvy.getComputedLogs = (flowIdOrElement) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const flow = flowController?.getFlow()
      const computedLogs = (window.Savvy.computed_logs || []).filter(
        (log) => log.flow_id === flow.id
      )
      return computedLogs
    }

    window.Savvy.watchFlowUpdates = (flowIdOrElement: string | Element | null, callback) => {
      if (!flowIdOrElement) flowIdOrElement = this.selectedFlow || null
      let flowId: string | null = null
      if (typeof flowIdOrElement === 'string') flowId = flowIdOrElement
      else flowId = flowIdOrElement?.getAttribute('id')?.replace('flow-', '') || null

      return this.getFlowController(flowIdOrElement || this.selectedFlow)?.watchFlowUpdates(
        (flow, prevFlow) => callback(flowId, flow, prevFlow)
      )
    }

    window.Savvy.watchFlowUpdatesOnce = (flowIdOrElement: string | Element | null, callback) => {
      if (!flowIdOrElement) flowIdOrElement = this.selectedFlow || null
      let flowId: string | null = null
      if (typeof flowIdOrElement === 'string') flowId = flowIdOrElement
      else flowId = flowIdOrElement?.getAttribute('id')?.replace('flow-', '') || null

      return this.getFlowController(flowIdOrElement || this.selectedFlow)?.watchFlowUpdatesOnce(
        (flow, prevFlow) => callback(flowId, flow, prevFlow)
      )
    }

    window.Savvy.watchFlowVersion = (flowIdOrElement: string | Element | null, callback) => {
      if (!flowIdOrElement) flowIdOrElement = this.selectedFlow || null
      let flowId: string | null = null
      if (typeof flowIdOrElement === 'string') flowId = flowIdOrElement
      else flowId = flowIdOrElement?.getAttribute('id')?.replace('flow-', '') || null

      return this.getFlowController(flowIdOrElement || this.selectedFlow)?.watchFlowVersion(
        (version, flow) => callback(flowId, version, flow)
      )
    }

    window.Savvy.triggerAction = window.Embeddables.triggerAction = (
      flowIdOrElement,
      action_id
    ) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const action = flowController?.dispatchAction(action_id, '')
      return !!action
    }

    window.Savvy.goToPage = window.Embeddables.goToPage = (
      flowIdOrElement: string | Element,
      idOrKeyOrIndex
    ) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const pageHandler = getPageNavHandlers(flowController)
      const pageDetails = {
        [typeof idOrKeyOrIndex === 'string' ? 'idOrKey' : 'index']: idOrKeyOrIndex,
      }

      const goToPage = pageHandler.goToPage(pageDetails)

      return !!goToPage
    }

    window.Savvy.goToNextPage = window.Embeddables.goToNextPage = (flowIdOrElement) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const next = flowController?.next()
      return !!next
    }

    window.Savvy.goToPrevPage = window.Embeddables.goToPrevPage = (flowIdOrElement) => {
      const flowController = this.getFlowController(flowIdOrElement || this.selectedFlow)
      const prev = flowController?.prev()
      return !!prev
    }

    window.Embeddables.switchToVersionV2 = ({
      embeddableId,
      version,
      stopFlowRender,
      forcedJson,
      branchId,
    }) =>
      this.getFlowController(embeddableId || this.selectedFlow)?.switchToVersion({
        version,
        stopFlowRender,
        forcedJson,
        branchId,
      }) || null

    window.Savvy.switchToVersion = window.Embeddables.switchToVersion = (
      flowIdOrElement,
      version,
      stopFlowRender,
      forcedJson
    ) =>
      this.getFlowController(flowIdOrElement || this.selectedFlow)?.switchToVersion({
        version,
        stopFlowRender,
        forcedJson,
      }) || null

    window.Savvy.getProdVersion = (flowId) => this.getProdVersionFunc(flowId)

    window.Savvy.getProdFlowJson = (flowId) => this.getProdFlowJsonFunc(flowId)

    window.Savvy.getStagingVersion = (flowId) => this.getStagingVersionFunc(flowId)

    window.Savvy.getStagingFlowJson = (flowId) => this.getStagingFlowJsonFunc(flowId)

    window.Savvy.getVersionHistory = (flowIdOrElement, limit?: number) => {
      return (
        this.getFlowController(flowIdOrElement || this.selectedFlowIdValue)?.getVersionHistory(
          limit
        ) || null
      )
    }

    window.Savvy.reRenderPage = window.Embeddables.reRenderPage = (
      flowIdOrElement,
      pageId?,
      preventWholeFlowRenderization = false
    ) => {
      const pageEvent = new CustomEvent('savvy:re_render_page', {
        detail: {
          flowId: typeof flowIdOrElement === 'string' ? flowIdOrElement : null,
          flowElement: typeof flowIdOrElement !== 'string' ? flowIdOrElement : null,
          pageId,
          forceWholePageRenderization: !preventWholeFlowRenderization,
        },
      })

      window.dispatchEvent(pageEvent)
    }

    window.Savvy.renderFlow = (value: {
      url: string
      method: string
      headers: { [key: string]: string }
      body: string | null
    }) => {
      if (Object.keys(value).length === 0) return
      const context: Context = {
        env: 'local',
        fetch: (...args: any[]) => fetch(args[0], args[1]),
        flowsBucket: {},
        flowsMetadataBucket: {},
        cmsR2Bucket: {},
      }
      const request = {
        url: new URL(value.url),
        method: value.method,
        headers: new Headers(value.headers),
        body: { json: value.body },
      }
      return handleRequest(context, request).then((res) => JSON.parse(res.responseData))
    }

    window.Savvy.getStaticHtml = window.Embeddables.getStaticHtml = (
      flowIdOrElement,
      pageId,
      forceFlowJson?
    ) =>
      this.getFlowController(flowIdOrElement || this.selectedFlow)?.getStaticHtml(
        pageId,
        forceFlowJson
      )

    window.Savvy.trackCustomEvent = window.Embeddables.trackCustomEvent = (
      flowId: string,
      customEventName: string,
      customEventProps?: Record<string, unknown>
    ) => {
      return trackCustomEvent(flowId, customEventName, customEventProps)
    }

    window.Embeddables.addCustomFont = ({ customFontTag }: { customFontTag: Tag }) => {
      //* Verify both type === 'font' and font_type === 'custom' to ensure it's a custom font
      const tagMetadata = customFontTag?.metadata as TagFontMetadata | undefined
      const isCustomFont = tagMetadata?.type === 'font' && tagMetadata?.font_type === 'custom'
      if (!isCustomFont) return Promise.resolve()

      return createAndInsertCustomFontTag({ tag: customFontTag })
    }
  }

  selectedFlowIdValueChanged(flowId: string) {
    const isEdit = isBuilderEditingMode()
    this.selectedFlow = isEdit ? flowId : null

    if (this.selectedFlowIdValue) {
      const flowController = this.getFlowController(this.selectedFlowIdValue)
      this.selectedFlowJson = flowController?.getFlow()
    } else {
      this.selectedFlowJson = null
    }
  }

  /**
   * This function will be called whenever the value of `resetWindowFunctions` changes.
   * If the value is true, it will reset the window.Savvy object and re-run the
   * `setupWindowFunctions` function on both the `global_controller` and `global_edit_controller`.
   */
  resetWindowFunctionsValueChanged(value: boolean) {
    if (value) {
      console.debug('reset')
      window.Savvy = {}
      this.setupWindowFunctions(true)
      const savvyEditor = document.querySelector('savvy-editor')
      if (savvyEditor) {
        this.application
          ?.getControllerForElementAndIdentifier(savvyEditor, 'global-edit')
          //@ts-ignore
          ?.setupWindowFunctions(true)
      }
      this.resetWindowFunctionsValue = false
    }
  }

  trySyncSelectedFlowFromSavvyHost() {
    if (this.selectedFlowIdValue) return
    const savvyEl = document.querySelector('savvy')
    if (!savvyEl) return
    const flowId = this.resolveFlowIdFromSavvyElement(savvyEl)
    if (flowId) this.selectedFlowIdValue = flowId
  }

  private resolveFlowIdFromSavvyElement(el: Element): string | null {
    const hostId = el.getAttribute('id')?.replace('flow-', '')
    if (hostId) return hostId
    if (!el.getAttribute('experiment-id')) return null
    return (
      el
        .querySelector('[data-controller~="flow"][data-flow-id-value]')
        ?.getAttribute('data-flow-id-value') ?? null
    )
  }

  getFlowController(flowIdOrElement: string | Element | null): FlowController | null {
    if (!flowIdOrElement) {
      console.error(`You must specify a Flow ID or pass a Flow element`)
    }

    if (typeof flowIdOrElement === 'string') {
      flowIdOrElement = document.querySelector(
        `[data-controller*="flow"][data-flow-id-value="${flowIdOrElement}"]`
      )
    }

    const controller =
      (flowIdOrElement &&
        (this.application.getControllerForElementAndIdentifier(
          flowIdOrElement,
          'flow'
        ) as FlowController | null)) ||
      null

    if (!controller) {
      console.error(`No Flow found "${flowIdOrElement}"`)
    }

    return controller
  }

  async getProdVersionFunc(flowId: string | null): Promise<{ versions: Version[] }> {
    const result = await this.fetchVersions(flowId, 'PROD')
    return result || { versions: [] }
  }

  async getStagingVersionFunc(flowId: string | null): Promise<{ versions: Version[] }> {
    const result = await this.fetchVersions(flowId, 'STAGING')
    return result || { versions: [] }
  }

  //LUCIAN created this function to avoid repeating code in "getProdVersionFunc" and in "getStagingVersionFunc"
  async fetchVersions(flowId: string | null, status: string) {
    if (!flowId) flowId = this.selectedFlow
    const supabaseClient = await initAndWaitForSupabaseClient()
    const { data: docs, error } = await supabaseClient
      .from('public_flow_versions')
      .select(
        'author_id, flow_title, version_number, group_id, status, updated_at, from_version_number'
      )
      .eq('flow_id', flowId)
      .eq('status', status)

    if (error) {
      console.error(`Error fetching ${status} docs:`, error)
      return null
    }

    // const versions: Version[] = docs.map((d) => {
    //   const author = {
    //     picture: d.author_picture,
    //     userId: d.author_id,
    //     userName: d.author_name,
    //   }
    //   return {
    //     author,
    //     form: d.flow_title as string,
    //     from_version: d.from_version_number as number,
    //     groupId: d.group_id as string,
    //     status: d.status as Status,
    //     updatedAt: d.updated_at as string,
    //     version: d.version_number as number,
    //   }
    // })

    return { versions: docs }
  }

  async getProdFlowJsonFunc(flowId: string | null) {
    return await this.fetchFlowJson(flowId, 'PROD')
  }

  async getStagingFlowJsonFunc(flowId: string | null) {
    return await this.fetchFlowJson(flowId, 'STAGING')
  }

  //LUCIAN created this function to avoid repeating code in "getProdFlowJsonFunc" and in "getStagingFlowJsonFunc"
  async fetchFlowJson(flowId: string | null, status: string) {
    if (!flowId) flowId = this.selectedFlow
    const supabaseClient = await initAndWaitForSupabaseClient()
    try {
      const { data, error } = await supabaseClient.storage
        .from('flows')
        .download(`${flowId}/${status}`)

      const json = JSON.parse((await data.text()) || '{}')
      return json
    } catch (err) {
      console.error(`Error in fetchFlowJson(${status}):`, err)
      return null
    }
  }
}
